import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

export interface AdvancedSearchPostFilters {
  mediaTypes?: string[];
  categories?: string[];
  tags?: string[];
  minLikes?: number;
  minViews?: number;
  dateFrom?: string;
  dateTo?: string;
  creatorIds?: string[]; // Filter by specific creators
  sortBy?: 'popular' | 'newest' | 'relevance';
}

export interface AdvancedSearchCreatorFilters {
  categories?: string[];
  isVerified?: boolean;
  minPatrons?: number;
  sortBy?: 'popular' | 'newest' | 'az';
}

export interface PostSearchHit {
  id: string;
  title?: string;
  content?: string;
  contentType: string;
  mediaUrls?: string[];
  publishedAt?: string;
  likeCount?: number;
  viewCount?: number;
  creator?: {
    id: string;
    username?: string;
    displayName?: string;
    avatar?: string;
  };
  highlight?: Record<string, string[]>;
}

export interface CreatorSearchHit {
  id: string;
  username: string;
  displayName?: string;
  bio?: string;
  avatar?: string;
  category?: string;
  patronCount?: number;
  postCount?: number;
  isVerified?: boolean;
  highlight?: Record<string, string[]>;
}

export interface SearchResponse<T> {
  hits: T[];
  total: number;
  page: number;
  limit: number;
  aggregations?: Record<string, any>;
}

export const advancedSearchService = {
  /**
   * Search posts using advanced filters
   */
  async searchPosts(
    query: string,
    filters?: AdvancedSearchPostFilters,
    page: number = 1,
    limit: number = 20
  ): Promise<SearchResponse<PostSearchHit>> {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(
      `${API_BASE_URL}/api/search/posts`,
      {
        query,
        ...filters,
        limit,
        page,
      },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      }
    );
    return response.data;
  },

  /**
   * Search creators using advanced filters
   */
  async searchCreators(
    query: string,
    filters?: AdvancedSearchCreatorFilters,
    page: number = 1,
    limit: number = 20
  ): Promise<SearchResponse<CreatorSearchHit>> {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(
      `${API_BASE_URL}/api/search/creators`,
      {
        query,
        ...filters,
        limit,
        page,
      },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      }
    );
    return response.data;
  },

  /**
   * Unified search across posts and creators
   */
  async unifiedSearch(
    query: string,
    types: ('posts' | 'creators')[],
    postFilters?: AdvancedSearchPostFilters,
    creatorFilters?: AdvancedSearchCreatorFilters,
    page: number = 1,
    limit: number = 20
  ): Promise<{
    posts?: SearchResponse<PostSearchHit>;
    creators?: SearchResponse<CreatorSearchHit>;
  }> {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(
      `${API_BASE_URL}/api/search/unified`,
      {
        query,
        types,
        postFilters,
        creatorFilters,
        limit,
        page,
      },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      }
    );
    return response.data;
  },

  /**
   * Get search suggestions as user types
   */
  async getSuggestions(
    query: string,
    type: 'posts' | 'creators' | 'all' = 'all'
  ): Promise<Array<{ type: string; name?: string; username?: string; title?: string }>> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/search/suggestions`, {
        params: { q: query, type },
      });
      return response.data?.suggestions || [];
    } catch {
      return [];
    }
  },

  /**
   * Export search results as CSV or JSON
   */
  async exportResults(
    query: string,
    format: 'csv' | 'json',
    filters?: AdvancedSearchPostFilters & AdvancedSearchCreatorFilters,
    type?: 'posts' | 'creators'
  ): Promise<Blob | any> {
    const params: any = { q: query, format, size: 500 };
    if (type) params.type = type;
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            params[key] = value.join(',');
          } else {
            params[key] = value;
          }
        }
      });
    }

    const response = await axios.get(`${API_BASE_URL}/api/search/export`, {
      params,
      responseType: format === 'csv' ? 'blob' : 'json',
    });

    return response.data;
  },
};
